import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Save, X, Search, FileSpreadsheet, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const Projections = () => {
  const navigate = useNavigate();
  const [projections, setProjections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingProjection, setEditingProjection] = useState(null);
  const [editValues, setEditValues] = useState({});
  
  // History dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  useEffect(() => {
    fetchProjections();
  }, [selectedYear]);

  const fetchProjections = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/wms/projections?year=${selectedYear}`);
      setProjections(response.data.projections || []);
    } catch (error) {
      toast.error('Failed to fetch projections');
    } finally {
      setLoading(false);
    }
  };

  const filteredProjections = projections.filter(p =>
    p.sku_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.design_name && p.design_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const openEditDialog = (projection) => {
    setEditingProjection(projection);
    const values = {};
    for (const p of projection.projections) {
      values[p.month] = p.qty;
    }
    setEditValues(values);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    try {
      await apiClient.put(`/wms/projections/${editingProjection.sku_code}/${editingProjection.year}`, {
        sku_code: editingProjection.sku_code,
        year: editingProjection.year,
        projections: editValues
      });
      toast.success('Projection updated');
      setEditDialogOpen(false);
      fetchProjections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update projection');
    }
  };

  const openHistoryDialog = (projection) => {
    setHistoryData(projection);
    setHistoryDialogOpen(true);
  };

  const getMonthQty = (projection, month) => {
    const p = projection.projections.find(p => p.month === month);
    return p ? p.qty.toLocaleString() : '0';
  };

  const getTotalQty = (projection) => {
    return projection.projections.reduce((sum, p) => sum + p.qty, 0).toLocaleString();
  };

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear - 2; i <= currentYear + 2; i++) {
    years.push(i);
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/wms')}
            className="text-[#F9FAFB] hover:bg-white/10 mr-4"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold font-display">Monthly Projections</h1>
            <p className="text-sm text-[#E8DCC8]">SKU-wise demand projections</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <Label>Year</Label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-32 mt-1" data-testid="year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label>Search</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by SKU or design name"
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => navigate('/admin/import')}
                className="border-[#2D5F4F] text-[#2D5F4F]"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Import Projections
              </Button>
            </div>
          </div>
        </div>

        {/* Projections Table */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-[#5A5F6E]">Loading...</div>
          ) : filteredProjections.length === 0 ? (
            <div className="p-12 text-center">
              <FileSpreadsheet className="w-12 h-12 text-[#E5DFD0] mx-auto mb-4" />
              <p className="text-[#5A5F6E]">No projections found for {selectedYear}.</p>
              <Button
                variant="link"
                onClick={() => navigate('/admin/import')}
                className="text-[#2D5F4F] mt-2"
              >
                Import projections →
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-[#E5DFD0]">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E] sticky left-0 bg-gray-50 z-10">SKU</th>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Design</th>
                    {months.map((m, i) => (
                      <th key={m} className="px-3 py-3 text-right font-medium text-[#1A1F2E] min-w-[80px]">{m}</th>
                    ))}
                    <th className="px-4 py-3 text-right font-medium text-[#1A1F2E] bg-blue-50">Total</th>
                    <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjections.map((projection, idx) => (
                    <tr key={projection.projection_id || idx} className="border-b border-[#E5DFD0] hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-[#1A1F2E] sticky left-0 bg-white z-10">
                        {projection.sku_code}
                      </td>
                      <td className="px-4 py-3 text-[#5A5F6E]">
                        {projection.design_name || '-'}
                      </td>
                      {months.map((m, i) => (
                        <td key={m} className="px-3 py-3 text-right text-[#5A5F6E]">
                          {getMonthQty(projection, i + 1)}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right font-bold text-[#1A1F2E] bg-blue-50">
                        {getTotalQty(projection)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(projection)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          {projection.revision_history?.length > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openHistoryDialog(projection)}
                              className="text-gray-600 hover:text-gray-700"
                            >
                              <History className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary */}
        {!loading && filteredProjections.length > 0 && (
          <div className="mt-4 text-sm text-[#5A5F6E]">
            Showing {filteredProjections.length} of {projections.length} projections
          </div>
        )}
      </main>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Projection - {editingProjection?.sku_code}</DialogTitle>
          </DialogHeader>
          {editingProjection && (
            <div className="py-4">
              <div className="grid grid-cols-4 gap-4">
                {months.map((m, i) => (
                  <div key={m}>
                    <Label className="text-xs">{m}</Label>
                    <Input
                      type="number"
                      value={editValues[i + 1] || 0}
                      onChange={(e) => setEditValues(prev => ({
                        ...prev,
                        [i + 1]: parseInt(e.target.value) || 0
                      }))}
                      className="mt-1"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleEditSave} className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white">
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Revision History - {historyData?.sku_code}</DialogTitle>
          </DialogHeader>
          {historyData?.revision_history && (
            <div className="py-4 space-y-4">
              {historyData.revision_history.map((rev, idx) => (
                <div key={idx} className="border border-[#E5DFD0] rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">
                      Revised on {new Date(rev.revised_at).toLocaleString()}
                    </span>
                    <span className="text-xs text-[#5A5F6E]">by {rev.revised_by}</span>
                  </div>
                  <div className="text-sm text-[#5A5F6E]">
                    <span className="font-medium">Previous values:</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {rev.old_projections?.map((p, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-100 rounded text-xs">
                          {months[p.month - 1]}: {p.qty.toLocaleString()}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projections;
